import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-how-dynamic-works';

/**
 * Explanatory section summarising a process or feature set.
 * Two-column layout: left column holds heading + body text, right column lists arrow-icon items with bold inline titles and descriptions.
 * Differs from sibling SignNow how-it-works variants by using arrow-icon bullets and an inline title-then-text item layout.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for how {keyword} works. 6-10 words.
 * @slot text - {content} <me-text> - Introductory paragraph for the step-by-step flow. You MUST write 15-30 words.
 * @example
 *   <sn-how-dynamic-works
 *     items='[{"title":"Identification of Parties","text":"Clearly state the names and roles."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">How it works</me-heading>
 *     <me-text slot="text">Understanding the key elements.</me-text>
 *   </sn-how-dynamic-works>
 */
@Component({
  tag: 'sn-how-dynamic-works',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcHowItWorksArrow {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Arrow-icon step items displayed on the right column.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Step action label. 1-5 words.
   * @item.property {string} text - Brief explanation of this step. 5-15 words.
   * @item.required title
   * @item.content title text
   * @required items
   * @minItems 4
   * @maxItems 4
   */
  @Prop({ mutable: true }) items?: { title?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} text - Fallback body text (use slot="text" instead)
   * @property {array} items - Fallback item list (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        raw_html?: string;
        items?: { title?: string; text?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="row">
          <me-column spanLg="5">
            <div class={`${COMPONENT_NAME}__left`}>
              <slot name="heading">
                {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
              </slot>
              <slot name="text">
                {values?.text && <me-text>{values.text}</me-text>}
              </slot>
            </div>
          </me-column>
          {items.length > 0 && (
            <me-column spanLg="7">
              <ul class={`${COMPONENT_NAME}__list`}>
                {items.map((item) => (
                  <li class={`${COMPONENT_NAME}__item`}>
                    <span class={`${COMPONENT_NAME}__arrow-box`} aria-hidden="true">
                      <me-icon
                        name="arrows--right-arrow-alt"
                        class={`${COMPONENT_NAME}__arrow`}
                        lazy={false}
                      ></me-icon>
                    </span>
                    <div class={`${COMPONENT_NAME}__item-content`}>
                      {item.title && <me-text tag="span" weight="bold" class={`${COMPONENT_NAME}__item-title`}>{item.title}:</me-text>}
                      {item.text && <me-text tag="span" class={`${COMPONENT_NAME}__item-text`}> {item.text}</me-text>}
                    </div>
                  </li>
                ))}
              </ul>
            </me-column>
          )}
        </me-row>
      </me-section>
    );
  }
}
